// Start page: lobby list with create/local/join-by-code on the left, profile and the members
// of the lobby under the mouse on the right (Design §9.1).
import { dot, el } from './dom.js';
import { guestName, profile, signIn, signOut } from './identity.js';
import { type Constants, playerColor } from './render.js';
import { openGuide } from './guide.js';
import { openStats } from './stats.js';
import { openWardrobe } from './wardrobe.js';
import { badgeIcon } from './badges.js';

const LIST_INTERVAL_MS = 3000;
/** This tab, so the server counts start page visitors (`present`) once each. */
const VISITOR = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
/** Shown once on the start page after being sent back (e.g. "Lobby nicht gefunden"). */
const MESSAGE_KEY = 'webtanks.message';

interface Listed {
  code: string;
  host: string;
  /** Names in slot order. */
  players: string[];
  /** Their colours and rank badges, same order. */
  colors: number[];
  ranks?: number[];
  spectators: string[];
  max_players: number;
  /** Teams (0 = off), anonymous colours, bots wanted. */
  teams?: number;
  anonymous?: boolean;
  bots?: number;
  number: number;
  rounds: number;
}

/** Icons of a lobby's settings left of its Join button (Wunsch User 2026-10-28). */
function modeIcons(l: Listed): HTMLElement[] {
  const icon = (text: string, title: string) => el('span', { textContent: text, title });
  return [
    l.teams ? icon(`👥${l.teams}`, `Team mode: ${l.teams} teams`) : '',
    l.anonymous ? icon('🎭', 'Anonymous colors') : '',
    l.bots ? icon('🤖', `Bots on (${l.bots})`) : '',
  ].filter((e): e is HTMLElement => e !== '');
}

const AUTH_ERRORS: Record<string, string> = {
  bad_name: 'Name: 3–16 characters (letters, digits, spaces, _ - .), not starting with “Guest”.',
  bad_password: 'Password: 8–128 characters.',
  name_taken: 'That name is already taken.',
  wrong: 'Wrong name or password.',
};

/** Profile box: guest name with login/register, or the account with its points (Design §10). */
async function showProfile(box: HTMLElement, consts: Constants): Promise<void> {
  let me;
  try {
    me = await profile();
  } catch {
    me = undefined; // server away: the lobby list says so
  }
  if (me) {
    const logout = el('button', { className: 'big', textContent: 'Log out' });
    logout.onclick = async () => {
      await signOut();
      showProfile(box, consts);
    };
    const wardrobe = el('button', { className: 'primary big', textContent: 'Cosmetics' });
    wardrobe.onclick = () => openWardrobe(me, consts, () => showProfile(box, consts));
    box.replaceChildren(
      el('h2', {}, me.name, me.place ? el('span', { className: 'place', title: 'Place on the leaderboard', textContent: ` #${me.place}` }) : ''),
      el(
        'p',
        {},
        'Rating ',
        el('span', { className: 'points', textContent: String(me.rating) }),
        ' / Reward ',
        el('span', { className: 'points', textContent: String(me.points) }),
      ),
      el('div', { className: 'row' }, wardrobe, logout),
    );
    return;
  }
  const name = el('input', { placeholder: 'Name', maxLength: 16, autocomplete: 'username' });
  const password = el('input', { type: 'password', placeholder: 'Password', autocomplete: 'current-password' });
  const error = el('p', { className: 'error' });
  const send = async (register: boolean) => {
    error.textContent = '';
    try {
      const code = await signIn(register, name.value, password.value);
      if (!code) return showProfile(box, consts);
      error.textContent = AUTH_ERRORS[code] ?? AUTH_ERRORS.wrong;
    } catch {
      error.textContent = 'Server not reachable.';
    }
  };
  const form = el(
    'form',
    { className: 'login', onsubmit: (e: SubmitEvent) => (e.preventDefault(), send(false)) },
    name,
    password,
    el(
      'div',
      { className: 'row' },
      el('button', { className: 'primary big', type: 'submit', textContent: 'Log in' }),
      el('button', { className: 'big', type: 'button', textContent: 'Sign up', onclick: () => send(true) }),
    ),
    error,
  );
  box.replaceChildren(
    el('h2', {}, guestName(), el('span', { className: 'tag', textContent: ' (Guest)' })),
    el('p', { className: 'hint', textContent: 'With an account you collect reward points for cosmetics.' }),
    form,
  );
}

/** Back to the start page with a message. */
export function backToMenu(message = ''): void {
  if (message) sessionStorage.setItem(MESSAGE_KEY, message);
  location.href = location.pathname;
}

/** `join('')` creates a new lobby. */
export function showMenu(status: HTMLElement, join: (code: string) => void, consts: Constants): void {
  const menu = document.getElementById('menu')!;
  status.hidden = true; // the big title replaces it

  const message = sessionStorage.getItem(MESSAGE_KEY);
  sessionStorage.removeItem(MESSAGE_KEY);

  const code = el('input', { placeholder: '•••••', maxLength: 5 });
  const go = (c: string) => {
    menu.hidden = true;
    clearInterval(timer);
    join(c);
  };
  const joinCode = () => code.value.trim() && go(code.value.trim().toUpperCase());
  code.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') joinCode();
  });
  const list = el('table', { className: 'list' });
  const listHead = el('h2', { textContent: 'Open lobbies' });
  const online = el('span', { className: 'v', textContent: '–' });
  const members = el('div', { className: 'box' });
  const profileBox = el('div', { className: 'box' });
  showProfile(profileBox, consts);

  /** Lobby whose members are shown: the last one under the mouse (or tapped). */
  let shown = '';
  function showMembers(l: Listed | undefined): void {
    shown = l?.code ?? '';
    for (const tr of list.querySelectorAll('tr')) tr.classList.toggle('shown', !!l && tr.dataset.code === l.code);
    if (!l) {
      members.replaceChildren(
        el('h2', { textContent: 'Players' }),
        el('p', { className: 'hint', textContent: 'Hover over a lobby.' }),
      );
      return;
    }
    members.replaceChildren(
      el('h2', { textContent: `Players – ${l.host}'s lobby` }),
      el('ul', {}, ...l.players.map((n, i) => el('li', {}, dot(playerColor(l.colors[i] ?? i)), badgeIcon(l.ranks?.[i] ?? 0) ?? '', n))),
      l.spectators.length > 0 ? el('h3', { textContent: 'Spectators' }) : '',
      l.spectators.length > 0 ? el('ul', {}, ...l.spectators.map((n) => el('li', { textContent: n }))) : '',
    );
  }

  menu.replaceChildren(
    el('h1', { className: 'title', textContent: 'webTanks' }),
    el(
      'div',
      { className: 'cols' },
      el(
        'div',
        { className: 'box main' },
        el(
          'div',
          { className: 'actions' },
          el('button', { className: 'primary big', textContent: 'Create lobby', onclick: () => go('') }),
          // One plate, so it only wraps as a whole.
          el(
            'span',
            { className: 'codeunit' },
            el('span', { className: 'lbl', textContent: 'Code' }),
            code,
            el('button', { textContent: 'Join', onclick: joinCode }),
          ),
          el('button', { className: 'big', textContent: 'Play locally', title: '2 players on one keyboard', onclick: () => (location.href = '?offline') }),
        ),
        message ? el('p', { className: 'error', textContent: message }) : '',
        listHead,
        list,
        el(
          'div',
          { className: 'actions foot' },
          el('button', { className: 'big', textContent: 'How to play', onclick: () => openGuide(consts) }),
          el('button', { className: 'big', textContent: 'Statistics', onclick: () => void openStats() }),
          el('span', { className: 'spacer' }),
          el(
            'span',
            { className: 'readout', title: 'In a lobby (private ones too, without bots) / on the site in total' },
            el('span', { className: 'k', textContent: 'Players online' }),
            online,
          ),
        ),
      ),
      el(
        'div',
        { className: 'side' },
        profileBox,
        members,
      ),
    ),
  );
  showMembers(undefined);
  menu.hidden = false;

  async function refresh(): Promise<void> {
    let lobbies: Listed[];
    try {
      const page: { online: number; present: number; lobbies: Listed[] } = await (await fetch(`/api/lobbies?visitor=${VISITOR}`)).json();
      lobbies = page.lobbies;
      online.textContent = `${page.online} / ${page.present}`;
    } catch {
      list.replaceChildren(el('tr', {}, el('td', { textContent: 'Server not reachable' })));
      online.textContent = '–';
      return;
    }
    if (lobbies.length === 0) {
      list.replaceChildren(el('tr', {}, el('td', { textContent: 'None – create one!' })));
    } else {
      list.replaceChildren(
        ...lobbies.map((l) => {
          const tr = el(
            'tr',
            {},
            el('td', { textContent: `${l.host}'s lobby` }),
            el('td', { className: 'num', textContent: `${l.players.length}/${l.max_players}` }),
            el('td', { textContent: l.number > 0 ? `Round ${l.number}/${l.rounds}` : 'waiting' }),
            el('td', { className: 'modes' }, ...modeIcons(l)),
            el(
              'td',
              {},
              el('button', { textContent: l.number > 0 ? 'Watch' : 'Join', onclick: () => go(l.code) }),
            ),
          );
          tr.dataset.code = l.code;
          tr.onmouseenter = () => showMembers(l);
          tr.onclick = () => showMembers(l); // touch has no hover
          return tr;
        }),
      );
    }
    showMembers(lobbies.find((l) => l.code === shown)); // fresh names, or gone
  }
  refresh();
  const timer = setInterval(refresh, LIST_INTERVAL_MS);
}
